import { MotionReveal } from "../../components/MotionReveal";
import { Building2, Shield, Activity, Network, FileCheck2 } from "lucide-react";

const painPoints = [
  {
    title: "Large, Distributed Infrastructure",
    desc: "Thousands of systems across departments, regions, data centers.",
    icon: Building2,
  },
  {
    title: "Legacy + Modern Systems",
    desc: "Mainframes coexisting with cloud services and digital citizen portals.",
    icon: Activity,
  },
  {
    title: "Compliance & Uptime Mandates",
    desc: "Zero tolerance for citizen-facing service failures with strict auditability.",
    icon: FileCheck2,
  },
  {
    title: "Limited Operational Visibility",
    desc: "Siloed teams and fragmented monitoring reduce time-to-decision.",
    icon: Network,
  },
];

const useCases = [
  {
    title: "National Data Centre Observability",
    bullets: [
      "Unified monitoring across government data centers",
      "Capacity planning & resource optimization",
      "Security & compliance integration",
    ],
  },
  {
    title: "Citizen Service Uptime Monitoring",
    bullets: [
      "Digital Experience Monitoring for portals",
      "SLA tracking & reporting",
      "Proactive issue detection",
    ],
  },
  {
    title: "Network & Application Correlation",
    bullets: [
      "End-to-end visibility across network and applications",
      "Faster troubleshooting",
      "Cross-departmental collaboration",
    ],
  },
  {
    title: "Predictive Outage Prevention",
    bullets: [
      "ML-based anomaly detection",
      "Early warning systems",
      "Automated remediation for common issues",
    ],
  },
  {
    title: "SLA & Compliance Dashboards",
    bullets: [
      "Real-time compliance status",
      "Audit-ready reporting",
      "Executive visibility",
    ],
  },
];

export default function GovernmentIndustryPage() {
  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
      <div className="pt-6 sm:pt-10">
        <MotionReveal>
          <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-white/3 p-8 sm:p-10">
            <div
              className="absolute inset-0"
              style={{
                background:
                  "radial-gradient(900px 360px at 20% 20%, rgba(0,212,255,0.18), transparent 60%), radial-gradient(760px 320px at 85% 55%, rgba(99,102,241,0.14), transparent 55%)",
              }}
            />
            <div className="relative">
              <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2 text-xs uppercase tracking-[0.18em] text-[var(--text-secondary)]">
                Government
              </div>
              <h1 className="mt-6 font-[var(--font-display)] text-4xl sm:text-5xl tracking-tight text-white">
                Government AIOps & Observability
              </h1>
              <p className="mt-4 max-w-3xl text-sm sm:text-base text-[var(--text-secondary)]">
                Reliable public services through intelligent IT operations.
              </p>
              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <a
                  href="/contact"
                  className="inline-flex items-center justify-center rounded-xl px-5 py-3 text-sm font-medium text-[#061019] bg-[var(--accent-primary)] hover:brightness-110"
                >
                  Request Government Assessment
                </a>
                <a
                  href="/roi"
                  className="inline-flex items-center justify-center rounded-xl px-5 py-3 text-sm font-medium text-white border border-white/15 bg-white/5 hover:bg-white/8"
                >
                  View ROI Model
                </a>
              </div>
            </div>
          </div>
        </MotionReveal>
      </div>

      <section className="mt-12 sm:mt-16">
        <MotionReveal>
          <div className="grid gap-10 lg:grid-cols-[0.45fr_0.55fr] lg:items-start">
            <div>
              <div className="text-xs uppercase tracking-[0.22em] text-[var(--text-secondary)]">The challenge</div>
              <h2 className="mt-3 font-[var(--font-display)] text-3xl sm:text-4xl tracking-tight text-white">
                Modernize without compromising continuity
              </h2>
              <p className="mt-4 text-sm sm:text-base text-[var(--text-secondary)]">
                Government environments require high availability, auditability, and resilience across heterogeneous legacy and cloud estates.
              </p>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              {painPoints.map((p, idx) => (
                <MotionReveal key={p.title} delay={Math.min(0.3, idx * 0.05)}>
                  <div className="glass-panel hover-lift rounded-3xl p-6">
                    <div className="flex items-start gap-4">
                      <div className="grid h-11 w-11 place-items-center rounded-2xl border border-white/10 bg-white/5 text-[var(--accent-primary)]">
                        <p.icon className="h-5 w-5" />
                      </div>
                      <div>
                        <div className="font-[var(--font-display)] text-lg tracking-tight text-white">{p.title}</div>
                        <div className="mt-2 text-sm text-[var(--text-secondary)]">{p.desc}</div>
                      </div>
                    </div>
                  </div>
                </MotionReveal>
              ))}
            </div>
          </div>
        </MotionReveal>
      </section>

      <section className="mt-14 sm:mt-18">
        <MotionReveal>
          <div>
            <div className="text-xs uppercase tracking-[0.22em] text-[var(--text-secondary)]">Use cases</div>
            <h2 className="mt-3 font-[var(--font-display)] text-3xl sm:text-4xl tracking-tight text-white">
              Government-ready observability outcomes
            </h2>
          </div>
        </MotionReveal>

        <div className="mt-8 grid gap-4 lg:grid-cols-2">
          {useCases.map((u, idx) => (
            <MotionReveal key={u.title} delay={Math.min(0.35, idx * 0.05)}>
              <div className="glass-panel hover-lift rounded-3xl p-6">
                <div className="font-[var(--font-display)] text-xl tracking-tight text-white">{u.title}</div>
                <ul className="mt-4 grid gap-2 text-sm text-[var(--text-secondary)]">
                  {u.bullets.map((b) => (
                    <li key={b} className="flex gap-3">
                      <span className="mt-2 h-1.5 w-1.5 rounded-full bg-[var(--accent-secondary)] opacity-80" />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </MotionReveal>
          ))}
        </div>
      </section>

      <section className="mt-16 sm:mt-20">
        <MotionReveal>
          <div className="glass-panel rounded-3xl p-8 sm:p-10">
            <div className="flex items-start gap-4">
              <div className="grid h-11 w-11 place-items-center rounded-2xl border border-white/10 bg-white/5 text-[var(--accent-secondary)]">
                <Shield className="h-5 w-5" />
              </div>
              <div>
                <div className="font-[var(--font-display)] text-2xl tracking-tight text-white">Outcomes</div>
                <div className="mt-4 grid gap-2 text-sm text-[var(--text-secondary)]">
                  <div>✓ Reduced service disruptions</div>
                  <div>✓ 50% faster incident response</div>
                  <div>✓ Improved public service delivery</div>
                  <div>✓ Regulatory compliance confidence</div>
                  <div>✓ Optimized IT spending</div>
                </div>
                <div className="mt-8 flex flex-col sm:flex-row gap-3">
                  <a
                    href="/contact"
                    className="inline-flex items-center justify-center rounded-xl px-5 py-3 text-sm font-medium text-[#061019] bg-[var(--accent-primary)] hover:brightness-110"
                  >
                    Transform Government IT Operations
                  </a>
                  <a
                    href="/capabilities"
                    className="inline-flex items-center justify-center rounded-xl px-5 py-3 text-sm font-medium text-white border border-white/15 bg-white/5 hover:bg-white/8"
                  >
                    Explore Capabilities
                  </a>
                </div>
              </div>
            </div>
          </div>
        </MotionReveal>
      </section>

      <div className="h-12" />
    </div>
  );
}
